import { useForm } from "react-hook-form";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
// components
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
// store
import { useAuthStore } from "../store/useAuthStore";
// services
import { useAuthService } from "../service/useAuthService";
// utils
import { getImageSrc } from "../utils/lib/utils";
// types
import { LoginFormInputs, loginSchema } from "../types/schema/LoginPageSchema";
// icons
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";

export default function LoginPage() {
  // Fetch existing organisation data
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const setRole = useAuthStore((state) => state.setRole);

  const authService = useAuthService();
  const onSubmit = (data: LoginFormInputs) => {
    setLoading(true);
    authService.mutate(data, {
      onSettled: () => {
        setLoading(false);
      },
      onSuccess: (response) => {
        localStorage.setItem("token", response.token); // Store the token in local storage
        // Handle successful login (e.g., redirect to dashboard)
        setRole(response.role); // Set the user role in the store
        const roleValue = response.role.replace(/\[|\]/g, "");
        localStorage.setItem("role", roleValue);

        const storedUsername = response.username || "User";

        if (storedUsername) {
          localStorage.setItem("userName", storedUsername);
        }

        navigate("/");
      },
      onError: (error: Error) => {
        console.error("Login failed:", error);
        toast.error(error.message);
      },
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A5EF9] via-[#2B6EFF] to-[#0F43C8] px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-7xl items-center justify-center md:min-h-[calc(100vh-5rem)]">
        <div className="relative w-full overflow-hidden rounded-[2.25rem] bg-white p-6 shadow-[0_30px_80px_rgba(10,47,140,0.38)] md:p-10 lg:p-14">
          <div className="pointer-events-none absolute -left-20 bottom-10 h-48 w-48 rounded-full bg-[#1A5EF9]/10 blur-2xl" />
          <div className="pointer-events-none absolute -right-16 -top-14 h-44 w-44 rounded-full bg-[#1A5EF9]/15 blur-2xl" />

          <div className="relative grid items-center gap-10 lg:grid-cols-[1.3fr_0.8fr]">
            {/* Left side - branding illustration */}
            <div className="relative hidden min-h-[460px] overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#f5f8ff] via-white to-[#eef4ff] lg:flex lg:items-center lg:justify-center">
              <div className="absolute left-12 top-12 h-24 w-24 rounded-full bg-[#1A5EF9]/10" />
              <div className="absolute bottom-10 right-16 h-28 w-28 rounded-full bg-[#1A5EF9]/15" />

              <div className="relative flex h-80 w-80 items-center justify-center rounded-full bg-white shadow-[0_20px_55px_rgba(26,94,249,0.18)]">
                <div className="absolute inset-4 rounded-full border border-[#1A5EF9]/15" />
                <img
                  src={getImageSrc("/images/login.png")}
                  alt="brand-logo"
                  className="h-70 w-70 rounded-full object-cover"
                />
              </div>

              <div className="absolute bottom-2 left-10 right-10 text-center ">
                <h2 className="text-2xl font-bold tracking-tight text-[#0d2e82] ">
                  {/* {organisationData?.name || "Welcome Back"} */}
                  Codenzic ERP
                </h2>
                <p className="mt-2 text-sm text-[#4964a5]">
                  Sign in to continue managing your business operations.
                </p>
              </div>
            </div>

            {/* Right side - login form */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mx-auto w-full max-w-sm rounded-[2rem] bg-gradient-to-br from-[#1A5EF9] to-[#4A82FF] p-7 text-white shadow-[0_18px_45px_rgba(14,50,145,0.35)] md:p-8"
            >
              <div className="mb-6 text-center">
                <p className="text-[11px] uppercase tracking-[0.28em] text-white/75">
                  Secure Access
                </p>
                <h1 className="mt-2 text-2xl font-semibold leading-tight">
                  Login your account
                </h1>
              </div>

              <div className="space-y-4">
                <Input
                  placeholder="Email"
                  className="h-11 rounded-full border border-white/20 bg-white/16 px-4 text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-white/60"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-sm text-red-100">{errors.email.message}</p>
                )}

                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    className="h-11 rounded-full border border-white/20 bg-white/16 px-4 pr-11 text-white placeholder:text-white/70 focus-visible:ring-2 focus-visible:ring-white/60"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-100">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* <div className="mt-3 text-right text-sm text-white/85">
                forget password?
              </div> */}

              {!loading ? (
                <Button
                  type="submit"
                  className="mt-5 h-11 w-full rounded-full bg-white text-[#1A5EF9] transition hover:bg-[#eaf1ff]"
                >
                  Login
                </Button>
              ) : (
                <Button
                  disabled
                  className="mt-5 h-11 w-full rounded-full bg-white/90 text-[#1A5EF9]"
                >
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Please wait
                </Button>
              )}

              {/* <p className="mt-5 text-center text-sm text-white/85">
                Create Account
              </p> */}
            </form>
          </div>
        </div>
      </div>
      <p className="text-center text-sm text-white/70 mt-1">
        Made with ❤️ by{" "}
        <a
          href="https://www.codenzic.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white underline underline-offset-2 hover:text-white/90"
        >
          Codenzic Innovation
        </a>
      </p>
    </div>
  );
}
